import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  ColumnSizingState,
  HeaderGroup,
  Header,
  Row,
  Cell,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Skeleton,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Collapse,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import {
  TableChart as TableIcon,
  ArrowUpward,
  ArrowDownward,
  FilterList,
  Search,
  Clear,
  ViewColumn,
  UnfoldMore,
} from '@mui/icons-material';
import { useAccount } from '../../../auth';

import { Widget } from '../../../types/canvas.types';
import { WidgetWrapper } from './WidgetWrapper';
import { useWidgetData } from '../../../data/canvas/useWidgetData';

interface TableWidgetProps {
  widget: Widget;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
}

// Format cell value based on type
const formatCellValue = (value: any, columnId: string): string => {
  if (value === null || value === undefined) return '';

  const keyLower = columnId.toLowerCase();

  // Format currency (multiple indicators)
  if (
    (keyLower.includes('price') ||
      keyLower.includes('revenue') ||
      keyLower.includes('cost') ||
      keyLower.includes('amount') ||
      keyLower.includes('salary') ||
      keyLower.includes('fee') ||
      keyLower.includes('total')) &&
    typeof value === 'number'
  ) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Format percentages
  if (
    (keyLower.includes('percent') ||
      keyLower.includes('rate') ||
      keyLower.includes('ratio')) &&
    typeof value === 'number'
  ) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100);
  }

  // Format regular numbers (but not IDs or codes)
  if (
    typeof value === 'number' &&
    !keyLower.includes('id') &&
    !keyLower.includes('code') &&
    !keyLower.includes('zip')
  ) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Format dates and datetimes
  if (
    (keyLower.includes('date') ||
      keyLower.includes('time') ||
      keyLower.includes('created') ||
      keyLower.includes('updated') ||
      keyLower.includes('modified')) &&
    (typeof value === 'string' || value instanceof Date)
  ) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        if (
          keyLower.includes('time') ||
          keyLower.includes('timestamp') ||
          (typeof value === 'string' && value.includes('T'))
        ) {
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(date);
        }
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }).format(date);
      }
    } catch {
      return value.toString();
    }
  }

  // Boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return value.toString();
};

export const TableWidget: React.FC<TableWidgetProps> = ({
  widget,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  // Get account ID from auth context
  const { currentAccount } = useAccount();
  const accountId = currentAccount?.id || '';

  // React-table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [showFilters, setShowFilters] = useState(false);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  // Server-side pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(
    (widget.configuration?.['pagination'] as any)?.pageSize || 25,
  );

  // Fetch data with server-side pagination and sorting
  const {
    data: realDataResult,
    isLoading: realDataLoading,
    error: realDataError,
  } = useWidgetData(widget, accountId, {
    enabled: !!accountId && !!widget.data_source,
    pagination: {
      page,
      pageSize,
    },
    sorting:
      sorting.length > 0
        ? {
            column: sorting[0].id,
            direction: sorting[0].desc ? 'desc' : 'asc',
          }
        : undefined,
  });

  // Extract data and metadata from result
  const data = realDataResult?.data || [];
  const totalCount = realDataResult?.totalCount;
  const skippedFilters = realDataResult?.skippedFilters || [];
  const hasRealData = widget.data_source && data && data.length > 0;
  const isLoading = realDataLoading;

  // Generate columns dynamically from data
  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    return Object.keys(firstRow).map((key) => ({
      id: key,
      accessorKey: key,
      header: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      cell: (info: any) => formatCellValue(info.getValue(), key),
      enableSorting: true,
      enableColumnFilter: true,
      enableResizing: true,
    }));
  }, [data]);

  // Initialize react-table
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnSizing,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    // Note: We use getFilteredRowModel for client-side filtering within the current page
    // but NOT getSortedRowModel because sorting is done server-side
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    manualPagination: true, // We handle pagination server-side
    manualSorting: true, // We handle sorting server-side
    pageCount: totalCount ? Math.ceil(totalCount / pageSize) : -1,
  });

  // Handle server-side pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when page size changes
  };

  // Clear all filters
  const handleClearFilters = () => {
    setColumnFilters([]);
  };

  // Column visibility menu handlers
  const handleOpenColumnMenu = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchor(null);
  };

  const handleToggleAllColumns = () => {
    table.toggleAllColumnsVisible();
  };

  return (
    <WidgetWrapper
      widget={widget}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    >
      {/* Show error message if real data failed but widget has data source */}
      {realDataError && widget.data_source && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">Failed to load data source.</Typography>
        </Alert>
      )}

      {/* Warning for skipped filters */}
      {skippedFilters.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1, fontSize: '0.75rem', py: 0.5 }}>
          Some filters not applied: {skippedFilters.join(', ')}
        </Alert>
      )}

      {isLoading || columns.length === 0 ? (
        <Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} height={40} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : !realDataError && data && data.length > 0 ? (
        <>
          {/* Toolbar */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" gap={1} alignItems="center">
              <Button
                size="small"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'contained' : 'outlined'}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button
                size="small"
                startIcon={<ViewColumn />}
                onClick={handleOpenColumnMenu}
                variant="outlined"
              >
                Columns
              </Button>
              {sorting.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {sorting.length === 1
                    ? `Sorted by ${sorting[0].id} (${sorting[0].desc ? 'desc' : 'asc'})`
                    : `Multi-sort: ${sorting.length} columns`}
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={1}>
              {sorting.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setSorting([])}
                  variant="text"
                  color="secondary"
                >
                  Clear Sort
                </Button>
              )}
              {columnFilters.length > 0 && (
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  variant="text"
                  color="secondary"
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>

          {/* Column Visibility Menu */}
          <Menu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={handleCloseColumnMenu}
          >
            <MenuItem onClick={handleToggleAllColumns}>
              <FormControlLabel
                control={<Checkbox checked={table.getIsAllColumnsVisible()} />}
                label="Toggle All"
              />
            </MenuItem>
            {table.getAllLeafColumns().map((column: any) => (
              <MenuItem
                key={column.id}
                onClick={() => column.toggleVisibility()}
              >
                <FormControlLabel
                  control={<Checkbox checked={column.getIsVisible()} />}
                  label={
                    typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : column.id
                  }
                />
              </MenuItem>
            ))}
          </Menu>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                {/* Column Headers with Sort */}
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(
                        (header: Header<any, unknown>) => (
                          <TableCell
                            key={header.id}
                            style={{ width: header.getSize() }}
                            sx={{
                              cursor: header.column.getCanSort()
                                ? 'pointer'
                                : 'default',
                              userSelect: 'none',
                              position: 'relative',
                              '&:hover': header.column.getCanSort()
                                ? {
                                    backgroundColor: 'action.hover',
                                  }
                                : {},
                            }}
                            onClick={() => {
                              if (header.column.getCanSort()) {
                                // Toggle sort direction manually for server-side sorting
                                const currentSort = sorting.find(
                                  (s) => s.id === header.column.id,
                                );
                                if (currentSort) {
                                  // Toggle direction
                                  setSorting([
                                    {
                                      id: header.column.id,
                                      desc: !currentSort.desc,
                                    },
                                  ]);
                                } else {
                                  // New sort, default to asc
                                  setSorting([
                                    { id: header.column.id, desc: false },
                                  ]);
                                }
                              }
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </Typography>
                              <Box display="flex" alignItems="center">
                                {header.column.getIsSorted() ? (
                                  <IconButton
                                    size="small"
                                    sx={{ ml: 0.5, p: 0 }}
                                  >
                                    {header.column.getIsSorted() === 'asc' ? (
                                      <ArrowUpward fontSize="small" />
                                    ) : (
                                      <ArrowDownward fontSize="small" />
                                    )}
                                  </IconButton>
                                ) : header.column.getCanSort() ? (
                                  <IconButton
                                    size="small"
                                    sx={{ ml: 0.5, p: 0, opacity: 0.3 }}
                                  >
                                    <UnfoldMore fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </Box>
                            </Box>
                            {/* Column Resizer */}
                            {header.column.getCanResize() && (
                              <Box
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  height: '100%',
                                  width: '5px',
                                  cursor: 'col-resize',
                                  userSelect: 'none',
                                  touchAction: 'none',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  ))}

                {/* Column Filters Row */}
                <TableRow>
                  <TableCell
                    colSpan={table.getAllLeafColumns().length}
                    sx={{ p: 0, border: 0 }}
                  >
                    <Collapse in={showFilters}>
                      <Box sx={{ p: 1, backgroundColor: 'action.hover' }}>
                        <Box
                          display="grid"
                          gridTemplateColumns={`repeat(${Math.min(
                            table
                              .getAllLeafColumns()
                              .filter((col: any) => col.getIsVisible()).length,
                            4,
                          )}, 1fr)`}
                          gap={1}
                        >
                          {table
                            .getAllLeafColumns()
                            .filter((column: any) => column.getIsVisible())
                            .map((column: any) => (
                              <TextField
                                key={column.id}
                                size="small"
                                placeholder={`Filter ${
                                  typeof column.columnDef.header === 'string'
                                    ? column.columnDef.header
                                    : column.id
                                }`}
                                value={
                                  (column.getFilterValue() as string) ?? ''
                                }
                                onChange={(e) =>
                                  column.setFilterValue(e.target.value)
                                }
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Search fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: column.getFilterValue() && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          column.setFilterValue('')
                                        }
                                      >
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row: Row<any>) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell: Cell<any, unknown>) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Server-side Pagination */}
          {hasRealData && totalCount !== undefined && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, 250]}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          textAlign="center"
        >
          <TableIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure a data source to display table data
          </Typography>
        </Box>
      )}
    </WidgetWrapper>
  );
};
